/* ───────────────── RichEditor: contentEditable + 풀 툴바 (value 동기화 포함) ───────────────── */
import React, { useEffect, useRef } from "react";

function ToolbarButton({ title, onClick, children, className = "" }) {
    return (
        <button
        type="button"
        className={"btn btn-outline-secondary " + className}
        title={title}
        onMouseDown={(e) => {
            e.preventDefault(); // selection 유지
            onClick();
        }}
        >
        {children}
        </button>
    );
}

function applyCmd(el, cmd, arg = null) {
    if (!el) return;
    const doc = el.ownerDocument || document; // 새 창 호환
    el.focus();
    doc.execCommand(cmd, false, arg);
}

export default function RichEditor({
    value = "",                 // HTML 또는 개행문자 포함 텍스트
    onChange,
    minHeight = 220,
    placeholder = "내용을 입력하세요",
    disabled = false,
}) {
    const boxRef = useRef(null);
    const composingRef = useRef(false);   // IME 조합 상태
    const lastHtmlRef = useRef("");       // 실제 DOM에 들어간 마지막 HTML

    const exec = (cmd, arg) => {
        if (disabled) return;
        applyCmd(boxRef.current, cmd, arg);
    };

    // 🔁 외부 value → 에디터 동기화
    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;

        // value가 순수 텍스트(태그 없음)라면 개행을 <br>로 치환
        const looksLikeHtml = /<[^>]+>/g.test(value);
        const html = looksLikeHtml ? value : (value || "").replace(/\n/g, "<br>");

        if (el.innerHTML !== html) {
            el.innerHTML = html;
            lastHtmlRef.current = html;
        }
    }, [value]);

    const emit = () => {
        if (!onChange) return;
        const html = boxRef.current?.innerHTML ?? "";
        lastHtmlRef.current = html;
        onChange(html);
    };

    const onInput = () => {
        if (disabled || composingRef.current) return;
        emit();
    };
    const onCompositionStart = () => { composingRef.current = true; };
    const onCompositionEnd = () => { composingRef.current = false; emit(); };

    // 이미지 삽입 (파일 선택)
    const fileInputRef = useRef(null);
    const openImagePicker = () => !disabled && fileInputRef.current?.click();
    const onPickImage = (e) => {
        if (disabled) return;
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
        exec("insertImage", reader.result); // base64 삽입
        e.target.value = "";
        emit();
    };
        reader.readAsDataURL(file);
    };

    // 붙여넣기 이미지 지원 (clipboard)
    const onPaste = (e) => {
        if (disabled) return;
        const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.indexOf("image") >= 0);
        if (!item) return; // 텍스트 등은 기본 동작
        e.preventDefault();
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = () => { exec("insertImage", reader.result); emit(); };
        reader.readAsDataURL(file);
    };

    // 드래그&드롭 이미지
    const onDrop = (e) => {
        if (disabled) return;
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (!file || !/^image\//.test(file.type)) return;
        const reader = new FileReader();
        reader.onload = () => { exec("insertImage", reader.result); emit(); };
        reader.readAsDataURL(file);
    };
    const onDragOver = (e) => { if (!disabled) e.preventDefault(); };

    const empty =
        !value || value === "<br>" || value === "<p><br></p>" || value === "&nbsp;";

    return (
        <div className="re-wrap">
        {/* 툴바 */}
        <div className="btn-group btn-group-sm mb-2 flex-wrap">
            <ToolbarButton title="되돌리기" onClick={() => exec("undo")}><i className="fa-solid fa-rotate-left" /></ToolbarButton>
            <ToolbarButton title="다시하기" onClick={() => exec("redo")}><i className="fa-solid fa-rotate-right" /></ToolbarButton>

            <ToolbarButton title="굵게" onClick={() => exec("bold")}><i className="fa-solid fa-bold" /></ToolbarButton>
            <ToolbarButton title="기울임" onClick={() => exec("italic")}><i className="fa-solid fa-italic" /></ToolbarButton>
            <ToolbarButton title="밑줄" onClick={() => exec("underline")}><i className="fa-solid fa-underline" /></ToolbarButton>
            <ToolbarButton title="취소선" onClick={() => exec("strikeThrough")}><i className="fa-solid fa-strikethrough" /></ToolbarButton>

            <ToolbarButton title="왼쪽정렬" onClick={() => exec("justifyLeft")}><i className="fa-solid fa-align-left" /></ToolbarButton>
            <ToolbarButton title="가운데정렬" onClick={() => exec("justifyCenter")}><i className="fa-solid fa-align-center" /></ToolbarButton>
            <ToolbarButton title="오른쪽정렬" onClick={() => exec("justifyRight")}><i className="fa-solid fa-align-right" /></ToolbarButton>

            <ToolbarButton title="글머리" onClick={() => exec("insertUnorderedList")}>•</ToolbarButton>
            <ToolbarButton title="번호목록" onClick={() => exec("insertOrderedList")}>1.</ToolbarButton>
            <ToolbarButton title="인용구" onClick={() => exec("formatBlock", "<blockquote>")}><i className="fa-solid fa-quote-left" /></ToolbarButton>
            <ToolbarButton title="제목 H2" onClick={() => exec("formatBlock", "<h2>")}>H2</ToolbarButton>
            <ToolbarButton title="제목 H3" onClick={() => exec("formatBlock", "<h3>")}>H3</ToolbarButton>
            <ToolbarButton title="수평선" onClick={() => exec("insertHorizontalRule")}><i className="fa-solid fa-minus" /></ToolbarButton>

            {/* 글자색/배경색/폰트크기 */}
            <label className="btn btn-outline-secondary mb-0">
            <i className="fa-solid fa-droplet me-1" />글자색
            <input type="color" className="d-none" onChange={(e) => exec("foreColor", e.target.value)} />
            </label>
            <label className="btn btn-outline-secondary mb-0">
            <i className="fa-solid fa-highlighter me-1" />배경색
            <input type="color" className="d-none" onChange={(e) => exec("backColor", e.target.value)} />
            </label>
            <select
            className="form-select form-select-sm w-auto"
            onChange={(e) => exec("fontSize", e.target.value)} // execCommand fontSize: 1~7
            defaultValue="3"
            style={{ marginLeft: 6 }}
            >
            <option value="2">작게</option>
            <option value="3">보통</option>
            <option value="4">크게</option>
            <option value="5">아주 크게</option>
            </select>

            <ToolbarButton
            title="링크"
            onClick={() => {
                const el = boxRef.current;
                const doc = el?.ownerDocument || document;
                const url = doc.defaultView?.prompt?.("링크 URL");
                if (url) exec("createLink", url);
        }}
            >
            <i className="fa-solid fa-link" />
            </ToolbarButton>
            <ToolbarButton title="링크 해제" onClick={() => exec("unlink")}><i className="fa-solid fa-link-slash" /></ToolbarButton>

            <ToolbarButton title="이미지" onClick={openImagePicker}><i className="fa-regular fa-image" /></ToolbarButton>
            <ToolbarButton title="비우기" className="btn-outline-danger" onClick={() => onChange && onChange("")}><i className="fa-regular fa-trash-can" /></ToolbarButton>
            <ToolbarButton title="서식 지우기" onClick={() => exec("removeFormat")}><i className="fa-solid fa-eraser" /></ToolbarButton>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onPickImage} />

        <div
            ref={boxRef}
            className="form-control"
            style={{ minHeight, overflow: "auto" }}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={onInput}
            onBlur={emit}
            onPaste={onPaste}
            onDrop={onDrop}
            onDragOver={(e) => { if (!disabled) e.preventDefault(); }}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
        />
        {empty && <div className="re-ph">{placeholder}</div>}

        <style>{`
            .re-wrap{ position:relative; }
            .re-ph{ position:absolute; left:12px; top:80px; color:#9aa1a9; pointer-events:none; font-size:.95rem; }
        `}</style>
        </div>
    );
}
